import { createApi } from '@reduxjs/toolkit/query/react';

import { logger } from '#shared';

import type { DomainEventFrame } from '#entities/DomainEvent';

import { fetchWithAuthBaseQuery } from '#shared/api';
import type { DataWithValidation } from '#shared/api';

import { parseCaseTimeline, parseCasesTimeline } from './schemas';

import type {
  CaseTimelineArgs,
  CaseTimelineCaseData,
  CaseTimelineRawEvent,
  CaseTimelineResult,
  CasesTimeline,
  CasesTimelineArgs,
} from '../model/types';

const HORIZON_MS = 24 * 60 * 60 * 1000;
const EPOCH_ISO = new Date(0).toISOString();

export const casesTimelinePath = (
  { sinceSeq }: CasesTimelineArgs,
  nowMs: number,
): string => {
  const params = new URLSearchParams({
    from: EPOCH_ISO,
    to: new Date(nowMs + HORIZON_MS).toISOString(),
  });
  if (sinceSeq !== undefined) {
    params.set('sinceSeq', sinceSeq);
  }
  return `api/cases/events?${params.toString()}`;
};

export const caseTimelinePath = (caseId: string): string =>
  `api/cases/${encodeURIComponent(caseId)}/events`;

const compareBigInt = (a: bigint, b: bigint): number =>
  a < b ? -1 : a > b ? 1 : 0;

const frameKey = (id: string): [bigint, bigint] => {
  const [seq, n = '0'] = id.split(':');
  return [BigInt(seq), BigInt(n)];
};

export const byFrameId = (a: DomainEventFrame, b: DomainEventFrame): number => {
  const [seqA, nA] = frameKey(a.id);
  const [seqB, nB] = frameKey(b.id);
  return compareBigInt(seqA, seqB) || compareBigInt(nA, nB);
};

/** The BE span order: `at`, then `seq`. */
export const bySpanOrder = (
  a: CaseTimelineRawEvent,
  b: CaseTimelineRawEvent,
): number =>
  Date.parse(a.at) - Date.parse(b.at) ||
  compareBigInt(BigInt(a.seq), BigInt(b.seq));

const isLaterSeq = (
  candidate: string | null,
  current: string | null,
): boolean =>
  candidate !== null &&
  (current === null || BigInt(candidate) > BigInt(current));

const appendCase = (
  current: CaseTimelineCaseData,
  incoming: CaseTimelineCaseData,
): void => {
  const seqs = new Set(current.events.map(event => event.seq));
  current.events.push(...incoming.events.filter(event => !seqs.has(event.seq)));
  current.events.sort(bySpanOrder);
  const ids = new Set(current.frames.map(frame => frame.id));
  current.frames.push(...incoming.frames.filter(frame => !ids.has(frame.id)));
  current.frames.sort(byFrameId);
  current.airLegs = incoming.airLegs;
  current.approvals = incoming.approvals;
  current.caseLabel = incoming.caseLabel ?? current.caseLabel;
};

const sorted = (entry: CaseTimelineCaseData): CaseTimelineCaseData => ({
  ...entry,
  events: [...entry.events].sort(bySpanOrder),
  frames: [...entry.frames].sort(byFrameId),
});

/**
 * A `sinceSeq` response carries only a case's newer events, so a case the cache lacks is not added from one;
 * `getCaseTimeline` loads it whole.
 */
export const mergeCasesTimeline = (
  current: CasesTimeline,
  incoming: CasesTimeline,
): void => {
  for (const entry of incoming.cases) {
    const known = current.cases.find(item => item.caseId === entry.caseId);
    if (known) {
      appendCase(known, entry);
    }
  }
  if (isLaterSeq(incoming.latestSeq, current.latestSeq)) {
    current.latestSeq = incoming.latestSeq;
  }
};

export const upsertCaseTimeline = (
  current: CasesTimeline,
  entry: CaseTimelineCaseData,
): void => {
  const index = current.cases.findIndex(item => item.caseId === entry.caseId);
  if (index === -1) {
    current.cases.push(sorted(entry));
  } else {
    appendCase(current.cases[index], entry);
  }
};

export const caseTimelineApi = createApi({
  reducerPath: 'caseTimelineApi',
  baseQuery: fetchWithAuthBaseQuery,
  refetchOnReconnect: true,
  endpoints: builder => ({
    getCasesTimeline: builder.query<
      DataWithValidation<CasesTimeline>,
      CasesTimelineArgs
    >({
      queryFn: async (arg, _api, _extra, baseQuery) => {
        const result = await baseQuery(casesTimelinePath(arg, Date.now()));
        if (result.error) {
          return { error: result.error };
        }
        const parsed = parseCasesTimeline(result.data);
        return {
          data: {
            ...parsed,
            data: { ...parsed.data, cases: parsed.data.cases.map(sorted) },
          },
        };
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (current, incoming, { arg }) => {
        if (arg.sinceSeq === undefined) {
          return incoming;
        }
        mergeCasesTimeline(current.data, incoming.data);
        current.validation = incoming.validation;
        return undefined;
      },
    }),
    getCaseTimeline: builder.query<
      DataWithValidation<CaseTimelineResult>,
      CaseTimelineArgs
    >({
      queryFn: async ({ caseId }, _api, _extra, baseQuery) => {
        const result = await baseQuery(caseTimelinePath(caseId));
        return result.error
          ? { error: result.error }
          : { data: parseCaseTimeline(result.data) };
      },
      keepUnusedDataFor: 0,
      onQueryStarted: async (
        { caseId },
        { dispatch, getState, queryFulfilled },
      ) => {
        const bulkSeq = (): string | null =>
          caseTimelineApi.endpoints.getCasesTimeline.select({})(
            getState() as Parameters<
              ReturnType<
                typeof caseTimelineApi.endpoints.getCasesTimeline.select
              >
            >[0],
          ).data?.data.latestSeq ?? null;
        const seqAtStart = bulkSeq();
        const { data } = await queryFulfilled.catch(() => {
          logger.error(`CaseTimeline: the read of case ${caseId} failed`);
          return { data: null };
        });
        const entry = data?.data.case;
        if (!data || !entry) {
          return;
        }
        dispatch(
          caseTimelineApi.util.updateQueryData(
            'getCasesTimeline',
            {},
            draft => {
              upsertCaseTimeline(draft.data, entry);
            },
          ),
        );
        // A `sinceSeq` poll that landed during this read skipped this case's newer rows, since the cache lacked the case.
        const seqNow = bulkSeq();
        if (
          isLaterSeq(seqNow, seqAtStart) &&
          isLaterSeq(seqNow, data.data.latestSeq)
        ) {
          void dispatch(
            caseTimelineApi.endpoints.getCaseTimeline.initiate(
              { caseId },
              { subscribe: false, forceRefetch: true },
            ),
          );
        }
      },
    }),
  }),
});

export const { useGetCasesTimelineQuery, useGetCaseTimelineQuery } =
  caseTimelineApi;
